import { useEffect, useMemo } from "react";
import { Platform, StyleSheet, View } from "react-native";
import Animated, {
  cancelAnimation,
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withTiming,
} from "react-native-reanimated";

import { PANE_THEMES } from "../brand/paneThemes";

const AMBER = PANE_THEMES.General.accent;

type FlyConfig = {
  key: string;
  leftPct: number;
  topPct: number;
  size: number;
  travel: number;
  driftMs: number;
  driftDelay: number;
  swayPx: number;
  pulseMs: number;
  pulseDelay: number;
};

function mulberry32(seed: number) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function Firefly({ cfg }: { cfg: FlyConfig }) {
  const drift = useSharedValue(0);
  const pulse = useSharedValue(0);

  useEffect(() => {
    drift.value = withDelay(
      cfg.driftDelay,
      withRepeat(withTiming(1, { duration: cfg.driftMs, easing: Easing.linear }), -1, false)
    );
    pulse.value = withDelay(
      cfg.pulseDelay,
      withRepeat(withTiming(1, { duration: cfg.pulseMs, easing: Easing.inOut(Easing.quad) }), -1, true)
    );
    return () => {
      cancelAnimation(drift);
      cancelAnimation(pulse);
    };
  }, [cfg.driftDelay, cfg.driftMs, cfg.pulseDelay, cfg.pulseMs, drift, pulse]);

  const style = useAnimatedStyle(() => {
    const ty = interpolate(drift.value, [0, 1], [0, -cfg.travel]);
    const tx = Math.sin(drift.value * Math.PI * 2 * 2.4) * cfg.swayPx;
    const op = interpolate(pulse.value, [0, 0.5, 1], [0.4, 0.82, 0.4]);
    const glow = cfg.size * 2.2;
    return {
      position: "absolute" as const,
      left: `${cfg.leftPct}%`,
      top: `${cfg.topPct}%`,
      width: cfg.size,
      height: cfg.size,
      borderRadius: cfg.size / 2,
      backgroundColor: AMBER,
      opacity: op,
      transform: [{ translateX: tx }, { translateY: ty }],
      boxShadow: `0 0 ${glow}px rgba(181,135,58,0.42)`,
    };
  });

  return <Animated.View pointerEvents="none" style={style} />;
}

export function Fireflies({ viewportHeight }: { viewportHeight: number }) {
  const configs = useMemo(() => {
    const rnd = mulberry32(20260426);
    const n = 18;
    const list: FlyConfig[] = [];
    for (let i = 0; i < n; i++) {
      list.push({
        key: `ff-${i}`,
        leftPct: rnd() * 88 + 6,
        topPct: rnd() * 78 + 10,
        size: 3 + Math.floor(rnd() * 3),
        travel: viewportHeight * (0.22 + rnd() * 0.38),
        driftMs: 15000 + Math.floor(rnd() * 10000),
        driftDelay: Math.floor(rnd() * 5000),
        swayPx: 4 + rnd() * 10,
        pulseMs: 3000 + Math.floor(rnd() * 2000),
        pulseDelay: Math.floor(rnd() * 2800),
      });
    }
    return list;
  }, [viewportHeight]);

  if (Platform.OS !== "web") return null;

  return (
    <View pointerEvents="none" style={[StyleSheet.absoluteFillObject, { zIndex: 1 }]}>
      {configs.map((c) => (
        <Firefly key={c.key} cfg={c} />
      ))}
    </View>
  );
}
